// src/components/DdosTesting/AttackMonitoring.jsx
import React, { useState } from 'react';

import { AnimatePresence, motion } from 'framer-motion'; // Import animation components

// Updated props
const AttackMonitoring = ({
    className,
    attackProgress, // Object containing { packetsSent, dataSent, serverPing, targetStatus, trafficRate, serverResponse }
    attackStatus, // 'idle', 'running', 'stopping', 'completed', 'error'
    serverInfo   // Basic server info for context
}) => {
    const isActive = attackStatus === 'running';
    const isIdle = attackStatus === 'idle' || attackStatus === 'completed' || attackStatus === 'error' || !attackStatus;

    // Format numbers nicely
    const formatNumber = (num) => {
        if (num === null || num === undefined) return 'N/A';
        if (num >= 1_000_000) return (num / 1_000_000).toFixed(1) + 'M';
        if (num >= 1_000) return (num / 1_000).toFixed(1) + 'K';
        return num.toString();
    };

    // Safely get values from progress or provide defaults/placeholders
    const packetsSent = attackProgress?.packetsSent;
    const dataSent = attackProgress?.dataSent ?? '0 MB';
    // Use progress ping if available (and is a number), else initial server ping
    const serverPing = typeof attackProgress?.serverPing === 'number' ? attackProgress.serverPing : (serverInfo?.ping ?? 'N/A');
    const targetStatus = attackProgress?.targetStatus ?? (serverInfo?.online ? 'Online' : 'Offline');
    const trafficRate = attackProgress?.trafficRate ?? { packetsPerSec: 0, bandwidth: '0 Mbps', cpuUsage: null };
    const serverResponse = attackProgress?.serverResponse ?? { responseTime: null, timeoutRate: 0, playersOnline: serverInfo?.players?.online ?? null };

    const getStatusColor = (status) => {
        switch (status?.toLowerCase()) {
            case 'online': return 'green';
            case 'running': return 'green';
            case 'stressed':
            case 'lagging': return 'orange';
            case 'offline':
            case 'crashed': return 'red';
            default: return 'gray';
        }
    };
    const targetStatusColor = getStatusColor(targetStatus);

    return (
        <div className={`bg-dark-600 rounded-2xl p-6 h-full flex flex-col transform-gpu hover:scale-[1.005] transition-medium border border-dark-400 hover:border-accent-red/30 relative overflow-hidden ${className}`}>
            {/* Background glow effects */}
            <div className="absolute -right-10 -top-10 w-32 h-32 bg-accent-red opacity-5 rounded-full blur-xl"></div>
            <div className="absolute -left-10 -bottom-10 w-32 h-32 bg-accent-blue opacity-5 rounded-full blur-xl"></div>

            <div className="flex justify-between items-center mb-5 relative z-10">
                <h3 className="font-bold text-lg flex items-center group">
                    <div className="w-8 h-8 rounded-lg bg-accent-red/20 flex items-center justify-center mr-2 transform-gpu group-hover:rotate-12 transition-medium">
                    <i className="fas fa-chart-line text-accent-red"></i>
                    </div>
                    Attack Monitoring
                </h3>
                <div
                    className="flex items-center px-2 py-1 rounded-lg transition-medium bg-dark-500"
                >
                    <span className={`w-2 h-2 bg-accent-${isActive ? 'green' : 'red'} rounded-full ${isActive ? 'animate-ping' : ''} mr-2`}></span>
                    <span className={`text-xs text-accent-${isActive ? 'green' : 'red'} transition-medium`}>
                        {isActive ? 'Active' : 'Not Active'}
                    </span>
                </div>
            </div>

            {/* Stats Grid */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6 relative z-10">
                {[
                    { label: 'Packets Sent', value: formatNumber(packetsSent), color: 'blue' },
                    { label: 'Data Sent', value: dataSent, color: 'purple' },
                    { label: 'Target Ping', value: typeof serverPing === 'number' ? `${serverPing} ms` : serverPing, color: 'orange' },
                    { label: 'Target Status', value: targetStatus, color: targetStatusColor }
                ].map((stat, index) => (
                    <div
                        key={index}
                        className={`bg-dark-500 rounded-lg p-3 transform-gpu hover:scale-105 transition-medium border border-dark-400 hover:border-accent-${stat.color}/50`}
                    >
                        <div className="text-xs text-gray-400 mb-1 hover:text-white transition-medium">{stat.label}</div>
                        <div className={`font-bold text-lg ${stat.color === 'gray' ? 'text-gray-300' : `text-accent-${stat.color}`}`}>{stat.value}</div>
                    </div>
                ))}
            </div>

            {/* Visualization Area */}
            <div className="bg-dark-800 rounded-xl flex-1 mb-4 p-4 min-h-[200px] sm:min-h-[300px] relative overflow-hidden border border-dark-500">
                <AnimatePresence mode="wait">
                    {isActive ? (
                        <motion.div
                            key="active-viz"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            transition={{ duration: 0.5 }}
                            className="absolute inset-0 flex items-center justify-center w-full"
                        >
                            <div className="relative w-full h-full flex items-center justify-center">
                                {/* Network visualization animation */}
                                <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
                                    <div className="w-40 h-40 rounded-full border-2 border-accent-red/30 animate-ping-slow"></div>
                                </div>
                                <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
                                    <div className="w-32 h-32 rounded-full border-2 border-accent-purple/30 animate-ping-slow delay-300"></div>
                                </div>
                                <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
                                    <div className="w-24 h-24 rounded-full border-2 border-accent-blue/30 animate-ping-slow delay-700"></div>
                                </div>

                                {/* Connection lines */}
                                {[...Array(10)].map((_, i) => (
                                    <motion.div
                                        key={i}
                                        className="absolute top-1/2 left-1/2 w-40 h-0.5 bg-gradient-to-r from-transparent via-accent-red/50 to-transparent"
                                        style={{ transformOrigin: 'left center' }}
                                        initial={{ rotate: i * 36, scaleX: 0 }}
                                        animate={{ scaleX: 1 }}
                                        transition={{ delay: i * 0.1, duration: 0.5 }}
                                    />
                                ))}

                                {/* Server icon */}
                                <motion.div
                                    initial={{ scale: 0 }}
                                    animate={{ scale: 1 }}
                                    // Use inset-0 and m-auto for robust centering of fixed size element
                                    className="absolute inset-0 m-auto w-12 h-12 rounded-lg bg-dark-600 border border-accent-red/50 flex items-center justify-center">
                                    <i className="fas fa-server text-accent-red animate-pulse-slow"></i>
                                </motion.div>
                            </div>
                        </motion.div>
                    ) : (
                        <motion.div
                            key="idle-viz"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            transition={{ duration: 0.5 }}
                            className="absolute inset-0 flex items-center justify-center"
                        >
                            <div className="text-center transform-gpu transition-medium group hover:scale-105">
                                <div className="w-24 h-24 mx-auto flex items-center justify-center mb-4 animate-float">
                                    <i className="fas fa-network-wired text-gray-600 text-5xl"></i>
                                </div>
                                <p className="text-gray-400 group-hover:text-white transition-medium">
                                    {attackStatus === 'completed' ? 'Attack completed. View results below.' :
                                    attackStatus === 'error' ? 'Attack ended due to an error.' :
                                    'Start attack to see network visualization'}
                                </p>
                            </div>
                        </motion.div>
                    )}
                </AnimatePresence>
            </div>

            {/* Progress Bars Area */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 relative z-10">
                {/* Traffic Rate */}
                <div className="bg-dark-500 rounded-lg p-4 transform-gpu hover:scale-[1.02] transition-medium border border-dark-400">
                     <h4 className="text-sm font-medium mb-3 flex items-center group">
                         <div className="w-6 h-6 rounded-lg bg-accent-blue/20 flex items-center justify-center mr-2 transform-gpu group-hover:rotate-12 transition-medium">
                             <i className="fas fa-tachometer-alt text-accent-blue"></i>
                         </div>
                         Traffic Rate
                     </h4>
                     {[
                         { label: 'Packets/sec', value: formatNumber(trafficRate?.packetsPerSec), color: 'blue', width: `${Math.min(100, (trafficRate?.packetsPerSec || 0) / 100)}%` }, // Example scaling
                         { label: 'Bandwidth', value: trafficRate?.bandwidth, color: 'purple', width: `${Math.min(100, parseFloat(trafficRate?.bandwidth || '0') / 10)}%` }, // Example scaling
                         { label: 'Est. CPU Usage', value: trafficRate?.cpuUsage ? `${trafficRate.cpuUsage}%` : 'N/A', color: 'green', width: `${trafficRate?.cpuUsage || 0}%` }
                     ].map((metric, index) => (
                       <div key={index} className="mb-3 last:mb-0 group">
                         <div className="flex justify-between text-xs mb-1">
                           <span className="text-gray-400 group-hover:text-white transition-medium">{metric.label}</span>
                           <span className="group-hover:text-accent-blue transition-medium">{metric.value}</span>
                         </div>
                         <div className="w-full h-2 bg-dark-400 rounded-full overflow-hidden">
                           <motion.div // Animate the bar width
                             className={`h-full bg-accent-${metric.color}`}
                             initial={{ width: 0 }}
                             animate={{ width: metric.width }}
                             transition={{ duration: 0.5, ease: 'easeOut' }}
                           />
                         </div>
                       </div>
                     ))}
                 </div>

                {/* Server Response */}
                <div className="bg-dark-500 rounded-lg p-4 transform-gpu hover:scale-[1.02] transition-medium border border-dark-400">
                     <h4 className="text-sm font-medium mb-3 flex items-center group">
                       <div className="w-6 h-6 rounded-lg bg-accent-red/20 flex items-center justify-center mr-2 transform-gpu group-hover:rotate-12 transition-medium">
                         <i className="fas fa-server text-accent-red"></i>
                       </div>
                       Server Response
                     </h4>
                     {[
                       { label: 'Response Time', value: serverResponse?.responseTime ? `${serverResponse.responseTime} ms` : 'N/A', color: 'green', width: `${Math.min(100, (serverResponse?.responseTime || 0) / 5)}%` }, // Example scaling
                       { label: 'Timeout Rate', value: `${serverResponse?.timeoutRate ?? 0}%`, color: 'red', width: `${serverResponse?.timeoutRate ?? 0}%` },
                       { label: 'Players Online', value: formatNumber(serverResponse?.playersOnline), color: 'yellow', width: `${Math.min(100, (serverResponse?.playersOnline || 0) / (serverInfo?.players?.max || 1000) * 100)}%` } // Scale based on max players
                     ].map((metric, index) => (
                       <div key={index} className="mb-3 last:mb-0 group">
                         <div className="flex justify-between text-xs mb-1">
                           <span className="text-gray-400 group-hover:text-white transition-medium">{metric.label}</span>
                           <span className="group-hover:text-accent-red transition-medium">{metric.value}</span>
                         </div>
                         <div className="w-full h-2 bg-dark-400 rounded-full overflow-hidden">
                           <motion.div // Animate the bar width
                             className={`h-full bg-accent-${metric.color}`}
                             initial={{ width: 0 }}
                             animate={{ width: metric.width }}
                             transition={{ duration: 0.5, ease: 'easeOut' }}
                           />
                         </div>
                       </div>
                     ))}
                 </div>
            </div>
        </div>
    );
  };

export default AttackMonitoring;